import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Check,
  X,
  MessageSquare,
  Calendar,
  Send,
  CheckCircle2,
  Clock,
  FileText,
  Eye,
  History,
  Pencil,
  ZoomIn,
  ChevronLeft,
  ChevronRight,
  Link as LinkIcon,
} from 'lucide-react';
import LinkedInPostPreview from '../../components/LinkedInPostPreview';
import WordDiff from '../../components/WordDiff';
import { getMediaType, looksLikeImage } from '../../lib/media';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature 4: Approval Stage Progress
const APPROVAL_STAGES = [
  { key: 'draft', label: 'Draft', icon: FileText },
  { key: 'internal_review', label: 'Internal Review', icon: Eye },
  { key: 'client_review', label: 'Your Review', icon: Clock },
  { key: 'approved', label: 'Approved', icon: CheckCircle2 },
  { key: 'scheduled', label: 'Scheduled', icon: Calendar },
];

const STATUS_TO_STAGE = {
  idea: 'draft', draft: 'draft',
  internal_review: 'internal_review',
  pending_approval: 'client_review', client_review: 'client_review',
  change_requested: 'client_review', rejected: 'client_review',
  approved: 'approved',
  scheduled: 'scheduled', posted: 'scheduled',
};

const StageProgressBar = ({ currentStage }) => {
  const getStageIndex = (stage) => {
    const mapped = STATUS_TO_STAGE[stage] || 'draft';
    return APPROVAL_STAGES.findIndex(s => s.key === mapped);
  };
  
  const currentIdx = getStageIndex(currentStage);
  
  return (
    <div className="flex items-center justify-between w-full px-2">
      {APPROVAL_STAGES.map((stage, idx) => {
        const Icon = stage.icon;
        const isCompleted = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        
        return (
          <React.Fragment key={stage.key}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                isCompleted ? 'bg-[#556B2F] border-[#556B2F] text-white' :
                isCurrent ? 'bg-[#3d4f22] border-[#3d4f22] text-white' :
                'bg-slate-100 border-slate-300 text-slate-400'
              }`}>
                {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
              </div>
              <span className={`text-xs mt-2 font-medium ${
                isCurrent || isCompleted ? 'text-[#3d4f22] dark:text-[#b8cf88]' : 'text-slate-400'
              }`}>
                {stage.label}
              </span>
            </div>
            {idx < APPROVAL_STAGES.length - 1 && (
              <div className={`flex-1 h-1 mx-2 rounded ${
                isCompleted ? 'bg-[#556B2F]' : 'bg-slate-200'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const ClientMediaItem = ({ url, idx, onOpenLightbox = null }) => {
  const [type, setType] = useState(getMediaType(url));

  if (type === 'video') {
    return (
      <video
        src={url}
        controls
        className="w-full h-48 object-cover rounded-lg bg-black"
      />
    );
  }

  if (type === 'pdf') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 hover:border-[#556B2F] transition-colors group"
      >
        <FileText className="w-5 h-5 text-red-500 shrink-0" />
        <span className="text-sm text-[#556B2F] hover:underline truncate">
          {url.split('/').pop() || 'Document.pdf'}
        </span>
      </a>
    );
  }

  if (type === 'link') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 hover:border-[#556B2F] transition-colors group"
      >
        <LinkIcon className="w-4 h-4 text-slate-400 group-hover:text-[#556B2F] shrink-0" />
        <span className="text-sm text-[#556B2F] hover:underline truncate">{url}</span>
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpenLightbox}
      className="relative group rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#556B2F]"
    >
      <img
        src={url}
        alt={`Image ${idx + 1}`}
        className="w-full h-48 object-cover bg-slate-100 transition-transform duration-200 group-hover:scale-105"
        referrerPolicy="no-referrer"
        onError={() => setType('link')}
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center">
        <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
      </div>
    </button>
  );
};

const ClientPostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [newComment, setNewComment] = useState('');

  // Suggested-edit (track-changes) state
  const [pendingEdit, setPendingEdit] = useState(null); // the client's pending version, if any
  const [editing, setEditing] = useState(false);
  const [draftCopy, setDraftCopy] = useState('');

  // Feature 5: Version History
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [showLinkedInPreview, setShowLinkedInPreview] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    fetchPost();
    fetchComments();
    fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API_URL}/posts/${postId}`);
      setPost(response.data.post);
      setPendingEdit(response.data.pending_edit || null);
    } catch (error) {
      toast.error('Failed to fetch post');
      navigate('/client/approvals');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API_URL}/posts/${postId}/comments`);
      setComments(response.data.comments);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const fetchVersions = async () => {
    try {
      const response = await axios.get(`${API_URL}/posts/${postId}/versions`);
      setVersions(response.data.versions);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
    }
  };

  // Suggested edits: client edits the full copy, which creates a pending
  // version for the agency to accept/decline. The live post is untouched.
  const startEditing = () => {
    setDraftCopy(pendingEdit ? pendingEdit.content : post?.copy || '');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraftCopy('');
  };

  const submitEdit = async () => {
    if (!draftCopy.trim() || draftCopy === post?.copy) {
      toast.error('Make a change to the text before submitting');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/posts/${postId}/suggest-edit`, { copy: draftCopy });
      toast.success('Your suggested changes were sent for approval');
      setEditing(false);
      setDraftCopy('');
      await Promise.all([fetchPost(), fetchVersions()]);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit changes');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/posts/${postId}/approve`);
      toast.success('Post approved!');
      navigate('/client/approvals');
    } catch (error) {
      toast.error('Failed to approve post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/posts/${postId}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Post rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      navigate('/client/approvals');
    } catch (error) {
      toast.error('Failed to reject post');
    } finally {
      setSubmitting(false);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      const response = await axios.post(`${API_URL}/posts/${postId}/comments`, { content: newComment });
      setComments([...comments, response.data.comment]);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'bg-slate-100 text-slate-700',
      internal_review: 'bg-[#d8e5b8] text-[#3d4f22]',
      client_review: 'bg-amber-100 text-amber-700',
      pending_approval: 'bg-amber-100 text-amber-700',
      approved: 'bg-[#d8e5b8] text-[#3d4f22]',
      rejected: 'bg-red-100 text-red-700',
      change_requested: 'bg-orange-100 text-orange-700',
      scheduled: 'bg-purple-100 text-purple-700',
      posted: 'bg-green-100 text-green-700'
    };
    return classes[status] || 'bg-slate-100 text-slate-700';
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const canTakeAction = post?.approval_stage === 'client_review' || post?.status === 'pending_approval' || post?.status === 'client_review';

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3d4f22]"></div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="space-y-6" data-testid="client-post-detail">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/client/approvals')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              Review Post
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={getStatusBadgeClass(post.approval_stage || post.status)}>
                {formatStatus(post.approval_stage || post.status)}
              </Badge>
            </div>
          </div>
        </div>

        {canTakeAction && !editing && (
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(true)}
              disabled={submitting}
              className="text-red-600 hover:bg-red-50"
              data-testid="reject-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button
              variant="outline"
              onClick={startEditing}
              disabled={submitting}
              className="text-[#3d4f22] dark:text-[#b8cf88] border-[#b8cf88] hover:bg-[#f0f4e8] dark:hover:bg-[#556B2F]/10"
              data-testid="suggest-edits-btn"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Suggest Edits
            </Button>
            <Button
              onClick={handleApprove}
              disabled={submitting}
              className="bg-[#3d4f22] hover:bg-[#3d4f22] text-white"
              data-testid="approve-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              {submitting ? 'Processing...' : 'Approve'}
            </Button>
          </div>
        )}
      </div>

      {/* Feature 4: Stage Progress Bar */}
      <Card className="bg-white dark:bg-slate-900 border-[#b8cf88] dark:border-slate-800 p-6">
        <StageProgressBar currentStage={post.approval_stage || post.status} />
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Pending suggested-edit banner */}
          {pendingEdit && !editing && (
            <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-900 dark:text-amber-200">
                    <Clock className="w-5 h-5" />
                    Your suggested changes are awaiting agency approval
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={startEditing}
                    className="w-full sm:w-auto"
                    data-testid="edit-again-btn"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit again
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-white/60 dark:bg-slate-900/40 p-4">
                  <WordDiff oldText={post.copy} newText={pendingEdit.content} />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Post Content */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Post Content</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 border-[#0a66c2] text-[#0a66c2] hover:bg-[#0a66c2]/5 dark:border-[#0a66c2] dark:text-[#5b9bd5]"
                  onClick={() => setShowLinkedInPreview(true)}
                >
                  <span className="font-bold text-xs leading-none bg-[#0a66c2] text-white rounded px-1 py-0.5">in</span>
                  Preview
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="space-y-3">
                  <Textarea
                    value={draftCopy}
                    onChange={(e) => setDraftCopy(e.target.value)}
                    rows={12}
                    className="text-base leading-relaxed text-slate-900 dark:text-slate-100"
                    placeholder="Edit the post copy..."
                    autoFocus
                    data-testid="edit-copy-input"
                  />
                  {draftCopy && draftCopy !== post.copy && (
                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-4">
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                        Your changes (<span className="line-through text-red-600 dark:text-red-400">removed</span>{' '}
                        <span className="underline text-[#3d4f22] dark:text-[#b8cf88]">added</span>)
                      </p>
                      <WordDiff oldText={post.copy} newText={draftCopy} />
                    </div>
                  )}
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="outline" onClick={cancelEditing} disabled={submitting}>
                      Cancel
                    </Button>
                    <Button
                      onClick={submitEdit}
                      disabled={submitting || !draftCopy.trim() || draftCopy === post.copy}
                      className="bg-[#3d4f22] hover:bg-[#2d3a18] text-white"
                      data-testid="submit-edit-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {submitting ? 'Submitting...' : 'Submit changes'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="prose prose-slate max-w-none whitespace-pre-wrap text-slate-700 dark:text-slate-300 leading-relaxed p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
                  data-testid="post-content"
                >
                  {post.copy}
                </div>
              )}

              {post.image_urls?.length > 0 && (() => {
                const imageOnlyUrls = post.image_urls.filter(looksLikeImage);
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                    {post.image_urls.map((url, idx) => {
                      const imageIdx = imageOnlyUrls.indexOf(url);
                      return (
                        <ClientMediaItem
                          key={idx}
                          url={url}
                          idx={idx}
                          onOpenLightbox={imageIdx >= 0 ? () => setLightboxIndex(imageIdx) : null}
                        />
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>


          {/* Comments */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Discussion
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {comments.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No comments yet. Start the conversation!</p>
              ) : (
                <ScrollArea className="max-h-64">
                  <div className="space-y-3">
                    {comments.map((comment) => (
                      <div key={comment.id} className={`p-3 rounded-lg ${
                        comment.user_role === 'client' 
                          ? 'bg-[#f0f4e8] dark:bg-[#556B2F]/10 ml-8' 
                          : 'bg-slate-50 dark:bg-slate-800 mr-8'
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{comment.user_name}</span>
                          <Badge variant="outline" className="text-xs capitalize">
                            {comment.user_role === 'client' ? 'You' : 'Agency'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-300">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}

              <div className="flex gap-2">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment or question..."
                  rows={2}
                  className="flex-1"
                  data-testid="comment-input"
                />
                <Button 
                  onClick={addComment} 
                  className="bg-[#3d4f22] hover:bg-[#3d4f22] text-white self-end"
                  data-testid="send-comment-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Post Details */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg">Post Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">Post Type</p>
                <p className="font-medium capitalize">{post.post_type?.replace(/_/g, ' ')}</p>
              </div>
              
              {post.scheduled_date && (
                <div>
                  <p className="text-sm text-slate-500">Scheduled For</p>
                  <p className="font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {format(new Date(post.scheduled_date), 'PPP')}
                  </p>
                </div>
              )}

              {post.review_deadline && (post.approval_stage === 'client_review' || post.status === 'client_review') && (() => {
                const hoursLeft = Math.max(0, Math.round((new Date(post.review_deadline) - Date.now()) / 3600000));
                const urgent = hoursLeft < 12;
                return (
                  <div className={`p-3 rounded-lg border ${urgent ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'}`}>
                    <p className={`text-xs font-medium flex items-center gap-1 ${urgent ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                      <Clock className="w-3 h-3" />
                      Review Deadline
                    </p>
                    <p className={`text-sm font-bold mt-1 ${urgent ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                      {hoursLeft === 0 ? 'Overdue' : hoursLeft < 24 ? `${hoursLeft}h remaining` : `${Math.round(hoursLeft / 24)}d remaining`}
                    </p>
                  </div>
                );
              })()}

            </CardContent>
          </Card>

          {/* Feature 5: Version History */}
          {versions.length > 0 && (
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <button 
                  onClick={() => setShowVersions(!showVersions)}
                  className="flex items-center justify-between w-full"
                >
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Version History
                  </CardTitle>
                  <Badge variant="outline">{versions.length} versions</Badge>
                </button>
              </CardHeader>
              
              {showVersions && (
                <CardContent>
                  <ScrollArea className="max-h-48">
                    <div className="space-y-2">
                      {versions.map((version) => (
                        <div 
                          key={version.id}
                          className="text-sm border-l-2 border-[#b8cf88] pl-3 py-1"
                        >
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">v{version.version}</Badge>
                            <span className="font-medium">{version.author_name}</span>
                          </div>
                          {version.summary && (
                            <p className="text-slate-500 text-xs mt-1">{version.summary}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            {format(new Date(version.created_at), 'PPp')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              )}
            </Card>
          )}

          {/* Stage History */}
          {post.stage_history?.length > 0 && (
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg">Approval History</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-48">
                  <div className="space-y-2">
                    {[...post.stage_history].reverse().map((entry, idx) => (
                      <div key={idx} className="text-sm border-l-2 border-slate-200 pl-3 py-1">
                        <p className="font-medium capitalize">{entry.stage?.replace(/_/g, ' ')}</p>
                        {entry.notes && <p className="text-slate-500 text-xs">{entry.notes}</p>}
                        <p className="text-xs text-slate-400">
                          {format(new Date(entry.changed_at), 'PPp')}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions (if reviewable) */}
          {canTakeAction && (
            <Card className="bg-[#f0f4e8] dark:bg-[#556B2F]/10 border-[#b8cf88] dark:border-[#3d4f22]">
              <CardContent className="p-4">
                <p className="text-sm text-[#3d4f22] dark:text-[#91a85e] mb-3">
                  This post is waiting for your review. You can:
                </p>
                <ul className="text-xs text-[#3d4f22] dark:text-[#6B8E23] space-y-1">
                  <li>• Select text to add inline feedback</li>
                  <li>• Approve if it's ready to publish</li>
                  <li>• Request changes for revisions</li>
                  <li>• Reject if it doesn't fit your brand</li>
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <X className="w-5 h-5 text-red-500" />
              Reject Post
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Please provide a reason for rejecting this post. The agency will be notified.
            </p>
            <div>
              <Label>Reason for Rejection</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="E.g., 'This doesn't align with our brand voice' or 'The messaging is off-brand'"
                rows={4}
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleReject}
              disabled={!rejectReason.trim() || submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-reject-btn"
            >
              {submitting ? 'Rejecting...' : 'Reject Post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LinkedIn Preview Dialog */}
      <Dialog open={showLinkedInPreview} onOpenChange={setShowLinkedInPreview}>
        <DialogContent className="max-w-xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <span className="font-bold text-sm bg-[#0a66c2] text-white rounded px-1.5 py-0.5">in</span>
              LinkedIn Post Preview
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-1">
            <LinkedInPostPreview
              content={post?.copy}
              attachedMedia={post?.image_urls?.find(url => {
                try { const ext = new URL(url).pathname.split('.').pop().toLowerCase(); return ['jpg','jpeg','png','gif','webp','avif'].includes(ext); } catch { return false; }
              }) || null}
              clientName={post?.client_name}
            />
            <p className="text-xs text-slate-400 text-center mt-3">
              Approximate preview — actual appearance may vary.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {lightboxIndex !== null && post?.image_urls?.length > 0 && (() => {
        const urls = post.image_urls.filter(looksLikeImage);
        const total = urls.length;
        const handleKey = (e) => {
          if (e.key === 'Escape') setLightboxIndex(null);
          if (e.key === 'ArrowRight') setLightboxIndex((i) => (i + 1) % total);
          if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i - 1 + total) % total);
        };
        return (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
            onClick={() => setLightboxIndex(null)}
            onKeyDown={handleKey}
            tabIndex={-1}
            ref={(el) => el?.focus()}
          >
            {/* Close */}
            <button
              type="button"
              onClick={() => setLightboxIndex(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Counter */}
            {total > 1 && (
              <span className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
                {lightboxIndex + 1} / {total}
              </span>
            )}

            {/* Prev */}
            {total > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i - 1 + total) % total); }}
                className="absolute left-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}

            {/* Image */}
            <img
              src={urls[lightboxIndex]}
              alt={`Image ${lightboxIndex + 1}`}
              className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
              referrerPolicy="no-referrer"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Next */}
            {total > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i + 1) % total); }}
                className="absolute right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default ClientPostDetail;
